import { useState, useEffect } from "react";
import { InputLabel } from "../../../../components/Input/InputLabel";
import { Color, Font } from "../../../../../core/models/entities/LogoOptions";
import LoadingElementIsolated from "../../../../components/loading-element/loading-element-isolated";

interface Props {
  font?: Font;
  color?: Color;
  value: string;
  heightMM: number;
  isValid: boolean;
  getPreview: (
    value: string,
    color: Color,
    heightMM: number,
    font: Font,
  ) => Promise<string>;
  fieldsPosition?: "above" | "below";
}

export function TextLogoPreview({
  font,
  value,
  color,
  heightMM,
  isValid,
  getPreview,
  fieldsPosition = "above",
}: Props) {
  const [preview, setPreview] = useState<string | undefined>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!isValid) {
      setPreview(undefined);
    }

    setIsLoading(isValid);
    const debounceTimeout = setTimeout(() => {
      if (isValid) {
        getPreview(value!, color!, heightMM!, font!)
          .then((result) => setPreview(result))
          .catch(() => setPreview(undefined))
          .finally(() => setIsLoading(false));
      } else {
        setPreview(undefined);
      }
    }, 1000);

    return () => clearTimeout(debounceTimeout);
  }, [color, font, heightMM, value, isValid]);

  const shouldShowFillMessage = !isValid && !isLoading;

  return (
    <div className={"space-y-0.5 mb-8"}>
      <InputLabel>Preview</InputLabel>
      {
        <div className={"flex rounded border h-32 justify-center items-center"}>
          {isLoading ? (
            <>
              <LoadingElementIsolated
                transparent
                className="h-32 animate-pulse"
              />
            </>
          ) : (
            <>
              {preview ? (
                <img src={`data:image/jpeg;base64,${preview}`} />
              ) : shouldShowFillMessage ? (
                <p className="px-4 text-center text-xs text-gray-500">
                  Fill out the fields {fieldsPosition} to generate a preview.
                </p>
              ) : null}
            </>
          )}
        </div>
      }
    </div>
  );
}
